import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    DestroyRef,
    Directive,
    ElementRef,
    inject,
    OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { filter } from 'rxjs';
import { SelectPanelKey } from './select-typeahead-panel-keys';

const TYPEAHEAD_DEBOUNCE_MILLISECONDS = 300;

/**
 * Allows `MatSelect` typeahead on multi-character code, not labels, using a buffer.
 */
@Directive({
    selector: 'mat-select[appSelectTypeahead]',
    standalone: true,
})
export class SelectTypeaheadDirective implements AfterViewInit, OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly matSelect = inject(MatSelect);
    private readonly ngControl = inject(NgControl, { self: true });

    private searchBuffer = '';
    private searchTimeout?: ReturnType<typeof setTimeout>;
    private pendingSelectionChange?: MatSelectChange;

    ngAfterViewInit(): void {
        // Capture printable keys before MatSelect's built-in typeahead handles them.
        this.elementRef.nativeElement.addEventListener(
            'keydown',
            this.handleKeydown,
            true,
        );
        this.elementRef.nativeElement.addEventListener(
            'focusout',
            this.handleFocusout,
            true,
        );
        this.document.addEventListener(
            'keydown',
            this.handlePanelKeydown,
            true,
        );

        this.matSelect.selectionChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.pendingSelectionChange = undefined;
                this.resetSearch();
            });

        this.matSelect.openedChange
            .pipe(
                filter((isOpen) => !isOpen),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.finishInteraction());
    }

    ngOnDestroy(): void {
        this.elementRef.nativeElement.removeEventListener(
            'keydown',
            this.handleKeydown,
            true,
        );
        this.elementRef.nativeElement.removeEventListener(
            'focusout',
            this.handleFocusout,
            true,
        );
        this.document.removeEventListener(
            'keydown',
            this.handlePanelKeydown,
            true,
        );
        this.resetSearch();
    }

    private readonly handleKeydown = (event: KeyboardEvent): void => {
        if (
            event.key.length !== 1 ||
            event.key === ' ' ||
            event.ctrlKey ||
            event.metaKey
        ) {
            this.resetSearch();
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        this.searchBuffer += event.key.toLowerCase();
        clearTimeout(this.searchTimeout);

        // Match after typing pauses so the search can contain one or more characters.
        this.searchTimeout = setTimeout(
            () => this.selectMatchingOption(),
            TYPEAHEAD_DEBOUNCE_MILLISECONDS,
        );
    };

    private readonly handleFocusout = (): void => {
        this.finishInteraction();
    };

    private readonly handlePanelKeydown = (event: KeyboardEvent): void => {
        if (this.isPanelNavigation(event)) {
            this.pendingSelectionChange = undefined;
            this.resetSearch();
            return;
        }

        if (!this.pendingSelectionChange || !this.matSelect.panelOpen) {
            return;
        }

        if (
            event.key === SelectPanelKey.Tab ||
            event.key === SelectPanelKey.Escape
        ) {
            this.matSelect.close();
            return;
        }

        if (event.key === SelectPanelKey.Enter) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.matSelect.close();
        }
    };

    private isPanelNavigation(event: KeyboardEvent): boolean {
        return (
            event.key.length > 1 &&
            event.key !== SelectPanelKey.Enter &&
            event.key !== SelectPanelKey.Escape &&
            event.key !== SelectPanelKey.Tab
        );
    }

    private selectMatchingOption(): void {
        const matchedOption = this.matSelect.options.find(
            (option) =>
                typeof option.value === 'string' &&
                option.value.toLowerCase().startsWith(this.searchBuffer),
        );
        if (matchedOption) {
            if (this.matSelect.value !== matchedOption.value) {
                this.ngControl.control?.markAsDirty();
                this.ngControl.control?.setValue(matchedOption.value);

                this.pendingSelectionChange = new MatSelectChange(
                    this.matSelect,
                    matchedOption.value,
                );

                if (!this.matSelect.panelOpen) {
                    this.emitPendingSelectionChange();
                }
            }

            this.document
                .getElementById(matchedOption.id)
                ?.scrollIntoView({ block: 'nearest' });
        }

        this.resetSearch();
    }

    private finishInteraction(): void {
        this.emitPendingSelectionChange();
        this.resetSearch();
    }

    private emitPendingSelectionChange(): void {
        const pendingSelectionChange = this.pendingSelectionChange;
        if (!pendingSelectionChange) {
            return;
        }

        // Emit selection change to notify any listeners (e.g. grid form controls)
        this.pendingSelectionChange = undefined;
        this.matSelect.selectionChange.emit(pendingSelectionChange);
    }

    private resetSearch(): void {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = undefined;
        this.searchBuffer = '';
    }
}
